import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, StopCircle, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { useStreamingMessage } from '@/lib/hooks/useStreamingMessage';
import { useFileUpload } from '@/lib/hooks/useFileUpload';
import AttachedFilesList from './AttachedFilesList';
import { getAcceptedFileTypes } from '@/lib/api/files';

interface ChatInputProps {
  conversationId: string | null;
}

function ChatInput({ conversationId }: ChatInputProps) {
  const [input, setInput] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { sendStreaming, isStreaming } = useStreamingMessage();

  const {
    files,
    hasFiles,
    allUploaded,
    isUploading,
    uploadedFileIds,
    handleFilesSelected,
    removeFile,
    retryUpload,
    clearAllFiles,
  } = useFileUpload({ conversationId });

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  const handleSubmit = async () => {
    // Need text OR files to submit
    if (!input.trim() && !hasFiles) return;

    // Wait for uploads to complete
    if (hasFiles && !allUploaded) {
      console.warn('Please wait for uploads to complete');
      return;
    }

    const message = input.trim();
    setInput('');

    // Build file info for optimistic display before clearing
    const filesForOptimistic = files
      .filter((f) => f.status === 'uploaded' && f.fileId)
      .map((f) => ({
        id: f.fileId!,
        filename: f.filename,
        mimeType: f.mimeType,
        sizeBytes: f.sizeBytes,
        downloadUrl: f.downloadUrl,
      }));

    // Capture fileIds before clearing
    const fileIdsToSend = [...uploadedFileIds];

    // Clear files immediately after capturing info
    clearAllFiles();

    try {
      await sendStreaming(
        message,
        conversationId ?? undefined,
        (newConversationId) => {
          if (!conversationId && newConversationId) {
            navigate(`/chat/${newConversationId}`, { replace: true });
          }
        },
        fileIdsToSend,
        filesForOptimistic
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setInput(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelected(e.target.files);
    // Reset input to allow selecting same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Disable send if streaming, uploading, or has files but not all uploaded
  const isSendDisabled = isStreaming || isUploading || (hasFiles && !allUploaded);
  // Disable attach only when streaming
  const isAttachDisabled = isStreaming;

  return (
    <div className="border-t border-gray-200 bg-card">
      {/* Attached Files List */}
      <AttachedFilesList
        files={files}
        onRemove={removeFile}
        onRetry={retryUpload}
      />

      {/* Input Area */}
      <div className="p-4">
        <div className="flex items-end gap-2 max-w-3xl mx-auto rounded-lg border-2 border-gray-300 bg-white p-2 focus-within:border-primary transition-colors">
          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getAcceptedFileTypes()}
            onChange={handleFileInputChange}
            className="hidden"
          />

          {/* Attach Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleAttachClick}
            disabled={isAttachDisabled}
            className="shrink-0"
            title="Allega file"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isStreaming}
            placeholder={
              hasFiles
                ? 'Aggiungi un messaggio (opzionale)...'
                : 'Scrivi un messaggio... (Shift+Invio per nuova riga)'
            }
            rows={1}
            className="flex-1 resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 max-h-[150px]"
          />

          {/* Send Button */}
          <Button
            variant="primary"
            size="icon"
            onClick={handleSubmit}
            disabled={(!input.trim() && !hasFiles) || isSendDisabled}
            className="shrink-0"
          >
            {isStreaming ? (
              <StopCircle className="h-4 w-4" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ChatInput;
